import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProjectUsageAlerts } from "Api/billingSystem/profile/getProjectUsageAlerts.request";
import { patchProjectUsageAlert } from "Api/billingSystem/profile/patchProjectUsageAlert.request";

import type { RootState } from "Store/configureStore";
import type {
  ProjectUsageAlert,
  ProjectUsageAlertUpdate
} from "Api/billingSystem/types";

// Thunks
export const getProjectUsageAlertsThunk = createAppAsyncThunk(
  "billingSystem/projectUsageAlerts/get",
  async (
    {
      billingProfileId,
      projectId
    }: { billingProfileId: string; projectId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getProjectUsageAlerts(billingProfileId, projectId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

export const updateProjectUsageAlertsThunk = createAppAsyncThunk(
  "billingSystem/projectUsageAlerts/update",
  async (
    {
      billingProfileId,
      projectId,
      alerts
    }: {
      billingProfileId: string;
      projectId: string;
      alerts: ProjectUsageAlertUpdate[];
    },
    { rejectWithValue }
  ) => {
    try {
      // The endpoint patches one alert at a time, so fan out a request per
      // changed type and wait for them all.
      return await Promise.all(
        alerts.map(alert =>
          patchProjectUsageAlert(billingProfileId, projectId, alert)
        )
      );
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State — keyed by projectId (a project maps to a single billing profile).
type ProjectUsageAlertsEntry = {
  alerts?: ProjectUsageAlert[];
  loading: boolean;
  loaded: boolean;
  error?: string;
  updating: boolean;
  updateError?: string;
};

type ProjectUsageAlertsState = {
  byProjectId: Record<string, ProjectUsageAlertsEntry | undefined>;
};

const initialState: ProjectUsageAlertsState = {
  byProjectId: {}
};

const projectUsageAlertsSlice = createSlice({
  name: "projectUsageAlerts",
  initialState,
  reducers: {
    clearProjectUsageAlertError(
      state,
      action: { payload: { projectId: string } }
    ) {
      const { projectId } = action.payload;
      setDeep(state, ["byProjectId", projectId, "error"], undefined);
      setDeep(state, ["byProjectId", projectId, "updateError"], undefined);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getProjectUsageAlertsThunk.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        setDeep(state, ["byProjectId", projectId, "loading"], true);
        setDeep(state, ["byProjectId", projectId, "error"], undefined);
      })
      .addCase(getProjectUsageAlertsThunk.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        setDeep(state, ["byProjectId", projectId, "loading"], false);
        setDeep(state, ["byProjectId", projectId, "loaded"], true);
        setDeep(state, ["byProjectId", projectId, "alerts"], action.payload);
      })
      .addCase(getProjectUsageAlertsThunk.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        setDeep(state, ["byProjectId", projectId, "loading"], false);
        setDeep(state, ["byProjectId", projectId, "loaded"], true);
        setDeep(
          state,
          ["byProjectId", projectId, "error"],
          action.payload?.error
        );
      })
      .addCase(updateProjectUsageAlertsThunk.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        setDeep(state, ["byProjectId", projectId, "updating"], true);
        setDeep(state, ["byProjectId", projectId, "updateError"], undefined);
      })
      .addCase(updateProjectUsageAlertsThunk.fulfilled, (state, action) => {
        const { projectId } = action.meta.arg;
        setDeep(state, ["byProjectId", projectId, "updating"], false);
        // Each PATCH returns the updated alert; merge them into the cache by
        // type so untouched alerts survive a partial update.
        const patched = (action.payload ?? []).filter(Boolean);
        if (patched.length) {
          const byType: Record<string, ProjectUsageAlert> = {};
          (state.byProjectId[projectId]?.alerts ?? []).forEach(alert => {
            byType[alert.type] = alert;
          });
          patched.forEach(alert => {
            byType[alert.type] = alert;
          });
          setDeep(
            state,
            ["byProjectId", projectId, "alerts"],
            Object.values(byType)
          );
        }
      })
      .addCase(updateProjectUsageAlertsThunk.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        setDeep(state, ["byProjectId", projectId, "updating"], false);
        setDeep(
          state,
          ["byProjectId", projectId, "updateError"],
          action.payload?.error
        );
      });
  }
});

// Selectors
const selectProjectUsageAlertsState = (state: RootState) =>
  state.projectUsageAlerts;

export const selectProjectUsageAlerts = createSelector(
  [
    selectProjectUsageAlertsState,
    (_: RootState, projectId: string) => projectId
  ],
  (state, projectId) => state.byProjectId[projectId]?.alerts
);

export const selectProjectUsageAlertsLoading = createSelector(
  [
    selectProjectUsageAlertsState,
    (_: RootState, projectId: string) => projectId
  ],
  (state, projectId) => state.byProjectId[projectId]?.loading ?? false
);

export const selectProjectUsageAlertsLoaded = createSelector(
  [
    selectProjectUsageAlertsState,
    (_: RootState, projectId: string) => projectId
  ],
  (state, projectId) => state.byProjectId[projectId]?.loaded ?? false
);

export const selectProjectUsageAlertsError = createSelector(
  [
    selectProjectUsageAlertsState,
    (_: RootState, projectId: string) => projectId
  ],
  (state, projectId) => state.byProjectId[projectId]?.error
);

export const selectProjectUsageAlertsUpdating = createSelector(
  [
    selectProjectUsageAlertsState,
    (_: RootState, projectId: string) => projectId
  ],
  (state, projectId) => state.byProjectId[projectId]?.updating ?? false
);

export const selectProjectUsageAlertsUpdateError = createSelector(
  [
    selectProjectUsageAlertsState,
    (_: RootState, projectId: string) => projectId
  ],
  (state, projectId) => state.byProjectId[projectId]?.updateError
);

export const { clearProjectUsageAlertError } = projectUsageAlertsSlice.actions;

export default projectUsageAlertsSlice.reducer;
